export type DataTableColumnPinGroup = 'left' | 'center' | 'right';

export interface FillColumnCandidate {
	id: string;
	pinGroup: DataTableColumnPinGroup;
}

export interface FillColumnLockCandidate extends FillColumnCandidate {
	canReorder: boolean;
}

/**
 * The fill column carries the flexible `1fr` track that absorbs slack. It is the
 * last column of the center partition, so left/right pinned groups stay glued to
 * their edges. Reorder only moves columns within the center partition, so as long
 * as the trailing center column is non-reorderable (e.g. `$add-field`), the fill
 * identity never moves and data columns can't flake mid-drag.
 */
export function selectFillColumnId(columns: FillColumnCandidate[]): string | undefined {
	const lastCenter = columns.findLast((column) => column.pinGroup === 'center');
	return lastCenter?.id ?? columns.at(-1)?.id;
}

/**
 * Returns the fill column id when the "reorder implies a locked trailing column"
 * invariant is violated — some column is reorderable while the fill column itself
 * is also reorderable, which would let the fill move mid-drag and resurrect the
 * flake. Returns `undefined` when the invariant holds (nothing reorderable, or the
 * fill column is locked). Backs a dev-time assertion.
 */
export function getReorderableFillColumnId(columns: FillColumnLockCandidate[]): string | undefined {
	if (!columns.some((column) => column.canReorder)) return undefined;

	const fillColumnId = selectFillColumnId(columns);
	const fillColumn = columns.find((column) => column.id === fillColumnId);

	return fillColumn?.canReorder ? fillColumnId : undefined;
}
